import {
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getProductOrderHistories } from "api/product.service";
import { formatDate, formatNZD } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import { DefaultTableLayout } from "pages/admin/dataEntry/TableGridDefinitions";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginatedProductOrderHistories } from "types/model.types";

const AdminProductOrderHistory: React.FC = () => {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [recordId, setRecordId] = useState<number | undefined>(undefined);
  const [activePage, setActivePage] = useState(1);
  const [loading, setloading] = useState<boolean>(false);
  const [transactions, setTransactions] =
    useState<PaginatedProductOrderHistories>({
      data: [],
      total: 1,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });

  useEffect(() => {
    if (!id) return;

    const numeric = Number(id);
    const isNumeric = !Number.isNaN(numeric);

    if (!isNumeric) {
      setTransactions({
        data: [],
        total: 1,
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
      return;
    }

    setRecordId(numeric);
    if (numeric !== recordId) setActivePage(1);
  }, [id]);

  useEffect(() => {
    if (!recordId) return;

    let mounted = true;
    setloading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getProductOrderHistories(Number(id), activePage, DEFAULT_PAGE_SIZE),
        );
        if (mounted && response) {
          setTransactions(response);
        }
      } catch (error) {
        logError(error);
      } finally {
        setloading(false);
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [activePage, recordId]);

  const rows = transactions.data.map((item, index) => {
    return (
      <Table.Tr key={`${item.id}-${index}`}>
        <Table.Td>{formatDate(item.createdOn)}</Table.Td>
        <Table.Td>{item.id}</Table.Td>
        <Table.Td>{item.supplierName}</Table.Td>
        <Table.Td className="number">{item.quantity}</Table.Td>
        <Table.Td className="number">{item.actual}</Table.Td>
        <Table.Td className="money">{formatNZD(item.oldSellPrice1)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.newSellPrice1)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.oldSellPrice2)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.newSellPrice2)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.oldCostPrice)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.newCostPrice)}</Table.Td>
        <Table.Td>{item.isProcessed ? "Yes" : "No"}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack mt={10}>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Text size="sm" c="dimmed">
              Showing 30 items per page
            </Text>
            <Pagination
              disabled={loading}
              total={Math.ceil(transactions.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
          <DefaultTableLayout>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Supplier Order #</Table.Th>
                <Table.Th>Supplier Name</Table.Th>
                <Table.Th className="number">Quantity</Table.Th>
                <Table.Th className="number">Actual</Table.Th>
                <Table.Th className="money">Old SP1</Table.Th>
                <Table.Th className="money">New SP1</Table.Th>
                <Table.Th className="money">Old SP2</Table.Th>
                <Table.Th className="money">New SP2</Table.Th>
                <Table.Th className="money">Old CP</Table.Th>
                <Table.Th className="money">New CP</Table.Th>
                <Table.Th>Order Processed</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </DefaultTableLayout>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              disabled={loading}
              total={Math.ceil(transactions.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
    </Stack>
  );
};

export default AdminProductOrderHistory;
